import React, { useState, useCallback } from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { bookingsAPI, usersAPI, slotsAPI } from '../../services/api';
import { formatDisplayName } from '../../utils/displayName';
import { eventDayKey } from '../../utils/eventDate';
import type { Booking, User, TrainingSlot, TrainingType } from '../../types';

interface BookingsModalProps {
  slotId: string;
  slotTitle: string;
  onClose: () => void;
}

const BookingsModal: React.FC<BookingsModalProps> = ({ slotId, slotTitle, onClose }) => {
  const { t } = useTranslation();
  const [editingBooking, setEditingBooking] = useState<string | null>(null);
  const [editingEvent, setEditingEvent] = useState(false);
  const [newUserId, setNewUserId] = useState('');
  const [addingParticipant, setAddingParticipant] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const queryClient = useQueryClient();

  useEscapeKey(onClose);

  const { data: bookings = [], isLoading } = useQuery({
    queryKey: ['bookings', slotId],
    queryFn: () => bookingsAPI.getSlotBookings(slotId),
  });

  const { data: slot } = useQuery({
    queryKey: ['slot', slotId],
    queryFn: () => slotsAPI.getSlot(slotId),
    enabled: editingEvent,
  });

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: usersAPI.getUsers,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string }) =>
      bookingsAPI.updateBooking(id, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings', slotId] });
      queryClient.invalidateQueries({ queryKey: ['slots'] });
      setEditingBooking(null);
      setNewUserId('');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: bookingsAPI.deleteBooking,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings', slotId] });
      queryClient.invalidateQueries({ queryKey: ['slots'] });
    },
  });

  const updateSlotMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<TrainingSlot> }) =>
      slotsAPI.updateSlot(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slot', slotId] });
      queryClient.invalidateQueries({ queryKey: ['slots'] });
      setEditingEvent(false);
    },
  });

  const adminCreateMutation = useMutation({
    mutationFn: ({ trainingSlotId, userId }: { trainingSlotId: string; userId: string }) =>
      bookingsAPI.adminCreateBooking(trainingSlotId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings', slotId] });
      queryClient.invalidateQueries({ queryKey: ['slots'] });
      setAddingParticipant(false);
      setSelectedUserId('');
    },
  });

  const bookedUserIds = new Set(bookings.map((b) => b.userId));
  const availableUsers = users.filter((u: User) => !bookedUserIds.has(u.id));

  const handleEdit = (booking: Booking) => {
    setEditingBooking(booking.id);
    setNewUserId(booking.userId);
  };

  const handleSave = (bookingId: string) => {
    if (newUserId) {
      updateMutation.mutate({ id: bookingId, userId: newUserId });
    }
  };

  const handleCancel = () => {
    setEditingBooking(null);
    setNewUserId('');
  };

  const handleEventSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data: Partial<TrainingSlot> = {
      type: formData.get('type') as TrainingType,
      title: formData.get('title') as string,
      description: formData.get('description') as string || undefined,
      date: formData.get('date') as string,
      startTime: formData.get('startTime') as string,
      endTime: formData.get('endTime') as string,
      capacity: parseInt(formData.get('capacity') as string),
    };
    updateSlotMutation.mutate({ id: slotId, data });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <div>
            <h3 className="text-xl font-semibold">
              {editingEvent ? t('admin.editEvent') : `${t('admin.bookingsFor')}: ${slotTitle}`}
            </h3>
            {!editingEvent && bookings.length > 0 && (
              <p className="text-sm text-gray-600 mt-1">
                {bookings.length} booking{bookings.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            {!editingEvent && (
              <>
                <button
                  onClick={() => { setAddingParticipant(true); setEditingBooking(null); }}
                  className="text-primary-600 hover:text-primary-800 font-medium text-sm"
                >
                  {t('admin.addParticipant')}
                </button>
                <button
                  onClick={() => setEditingEvent(true)}
                  className="text-primary-600 hover:text-primary-800 font-medium text-sm"
                >
                  {t('admin.editEvent')}
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 p-6">
          {editingEvent && slot ? (
            <form onSubmit={handleEventSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('admin.type')}
                  </label>
                  <select
                    name="type"
                    required
                    className="w-full border rounded px-3 py-2"
                    defaultValue={slot.type}
                  >
                    <option value="SWIMMING">{t('admin.typeSwimming')}</option>
                    <option value="BIKING">{t('admin.typeBiking')}</option>
                    <option value="RUNNING">{t('admin.typeRunning')}</option>
                    <option value="GYM">{t('admin.typeGym')}</option>
                    <option value="FREE">{t('admin.typeFree')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('admin.title')}
                  </label>
                  <input
                    type="text"
                    name="title"
                    required
                    className="w-full border rounded px-3 py-2"
                    defaultValue={slot.title}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('admin.date')}
                  </label>
                  <input
                    type="date"
                    name="date"
                    required
                    className="w-full border rounded px-3 py-2"
                    defaultValue={eventDayKey(slot.date)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('admin.startTime')}
                  </label>
                  <input
                    type="time"
                    name="startTime"
                    required
                    className="w-full border rounded px-3 py-2"
                    defaultValue={slot.startTime}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('admin.endTime')}
                  </label>
                  <input
                    type="time"
                    name="endTime"
                    required
                    className="w-full border rounded px-3 py-2"
                    defaultValue={slot.endTime}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('admin.capacity')}
                  </label>
                  <input
                    type="number"
                    name="capacity"
                    required
                    min="1"
                    className="w-full border rounded px-3 py-2"
                    defaultValue={slot.capacity}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.description')}
                </label>
                <textarea
                  name="description"
                  className="w-full border rounded px-3 py-2"
                  rows={3}
                  defaultValue={slot.description || ''}
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={updateSlotMutation.isPending}
                  className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700 disabled:opacity-50"
                >
                  {updateSlotMutation.isPending ? t('admin.saving') : t('admin.save')}
                </button>
                <button
                  type="button"
                  onClick={() => setEditingEvent(false)}
                  className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
                >
                  {t('admin.cancel')}
                </button>
              </div>
            </form>
          ) : (
            <>
              {addingParticipant && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg border flex items-center gap-3">
                  <select
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    className="border rounded px-3 py-2 text-sm flex-1"
                  >
                    <option value="">{t('admin.selectUser')}</option>
                    {availableUsers.map((user: User) => (
                      <option key={user.id} value={user.id}>
                        {formatDisplayName(user)} ({user.role})
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => {
                      if (selectedUserId) {
                        adminCreateMutation.mutate({ trainingSlotId: slotId, userId: selectedUserId });
                      }
                    }}
                    disabled={!selectedUserId || adminCreateMutation.isPending}
                    className="bg-primary-600 text-white px-4 py-2 rounded text-sm hover:bg-primary-700 disabled:opacity-50"
                  >
                    {adminCreateMutation.isPending ? t('admin.adding') : t('admin.add')}
                  </button>
                  <button
                    onClick={() => { setAddingParticipant(false); setSelectedUserId(''); }}
                    className="text-gray-600 hover:text-gray-800 text-sm font-medium"
                  >
                    {t('admin.cancel')}
                  </button>
                  {adminCreateMutation.isError && (
                    <span className="text-red-600 text-sm">
                      {(adminCreateMutation.error as any)?.response?.data?.error || 'Failed to add'}
                    </span>
                  )}
                </div>
              )}
              {isLoading ? (
                <div className="text-center text-gray-500">{t('admin.loadingBookings')}</div>
              ) : bookings.length === 0 ? (
                <div className="text-center text-gray-500">{t('admin.noBookings')}</div>
              ) : (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          {t('admin.user')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          {t('admin.type')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          {t('admin.bookedOn')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          {t('admin.actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {bookings.map((booking) => (
                        <tr key={booking.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {editingBooking === booking.id ? (
                              <select
                                value={newUserId}
                                onChange={(e) => setNewUserId(e.target.value)}
                                className="border rounded px-2 py-1 text-sm"
                              >
                                <option value="">{t('admin.selectUser')}</option>
                                {users.map((user: User) => (
                                  <option key={user.id} value={user.id}>
                                    {formatDisplayName(user)} ({user.role})
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <div className="text-sm font-medium text-gray-900">
                                {booking.user ? formatDisplayName(booking.user) : 'Unknown'}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {booking.isDefault ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                {t('admin.typeDefault')}
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {t('admin.typeRegular')}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {format(new Date(booking.createdAt), 'dd MMM yyyy')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {editingBooking === booking.id ? (
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleSave(booking.id)}
                                  className="text-primary-600 hover:text-primary-800 font-medium"
                                >
                                  {t('admin.save')}
                                </button>
                                <button
                                  onClick={handleCancel}
                                  className="text-gray-600 hover:text-gray-800 font-medium"
                                >
                                  {t('admin.cancel')}
                                </button>
                              </div>
                            ) : (
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleEdit(booking)}
                                  className="text-primary-600 hover:text-primary-800 font-medium"
                                >
                                  {t('admin.edit')}
                                </button>
                                <button
                                  onClick={() => {
                                    if (window.confirm(t('admin.deleteBookingConfirm'))) {
                                      deleteMutation.mutate(booking.id);
                                    }
                                  }}
                                  className="text-accent-600 hover:text-accent-800 font-medium"
                                >
                                  {t('admin.delete')}
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
              )}
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
          >
            {t('admin.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingsModal;
